import { videosNoTexto } from "./video";
import { lerItem, gravarItem } from "./armazenamentoSeguro";

// As conversas ficam no localStorage do navegador (~5 MB por site no Chrome). A SAK manda o
// artigo inteiro (HTML, até ~9 mil caracteres) em cada um dos 5 artigos da resposta; guardar
// isso enchia o espaço em um dia de uso, e a resposta que não cabia virava "Não consegui
// buscar a resposta agora".

const CHAVE = "conversas_storage";
const TAMANHO_RESUMO = 300;

type Conversas = Record<string, Record<string, any[]>>;

const resumirHtml = (html: string) => {
  const texto = new DOMParser().parseFromString(html, "text/html").documentElement.textContent || "";
  const limpo = texto.replace(/\s+/g, " ").trim();
  return limpo.length > TAMANHO_RESUMO ? `${limpo.slice(0, TAMANHO_RESUMO)}…` : limpo;
};

const urlDoVideo = ({ plataforma, id }: { plataforma: string; id: string }) =>
  plataforma === "tella" ? `https://www.tella.tv/video/${id}` : `https://www.youtube.com/embed/${id}`;

// Guarda do artigo só o que a tela usa: título, link, resumo do cartão e o primeiro vídeo.
export const compactarArtigo = (artigo: any) => {
  if (!artigo || typeof artigo !== "object") return artigo;
  const descricao = typeof artigo.descricao === "string" ? artigo.descricao : "";
  if (descricao.length <= TAMANHO_RESUMO + 1 && !/<[a-z]/i.test(descricao)) return artigo;

  const [video] = videosNoTexto(descricao);
  return {
    nomeartigo: artigo.nomeartigo,
    link: artigo.link,
    ...(artigo["link-embed"] ? { "link-embed": artigo["link-embed"] } : {}),
    descricao: resumirHtml(descricao),
    ...(artigo.video ? { video: artigo.video } : video ? { video: urlDoVideo(video) } : {}),
  };
};

const compactarTudo = (conversas: Conversas) => {
  for (const app of Object.values(conversas)) {
    for (const mensagens of Object.values(app)) {
      for (const mensagem of mensagens) {
        if (Array.isArray(mensagem?.artigos)) mensagem.artigos = mensagem.artigos.map(compactarArtigo);
        // O artigo completo (até 20 mil caracteres) é o primeiro a sair: sem ele, o botão
        // "Ver artigo completo" vira um link para a central da SAK.
        if (mensagem?.artigoHtml) delete mensagem.artigoHtml;
      }
    }
  }
};

const ehFaltaDeEspaco = (erro: unknown) =>
  erro instanceof DOMException &&
  (erro.name === "QuotaExceededError" || erro.name === "NS_ERROR_DOM_QUOTA_REACHED");

export const lerConversas = (): Conversas => {
  try {
    return JSON.parse(lerItem(CHAVE) || "{}");
  } catch {
    return {};
  }
};

// Apaga uma conversa (e o app do storage, se era a última dele). Só existe esta cópia: a IA
// não guarda as conversas.
export const excluirConversa = (app: string, conversa: string) => {
  const conversas = lerConversas();
  if (!conversas[app]?.[conversa]) return;
  delete conversas[app][conversa];
  if (!Object.keys(conversas[app]).length) delete conversas[app];
  gravarItem(CHAVE, JSON.stringify(conversas));
};

// Grava as conversas. Se faltar espaço: 1) compacta as mensagens antigas (salvas antes desta
// versão, com o HTML inteiro dos artigos); 2) apaga as conversas mais antigas, uma a uma,
// preservando a conversa atual.
export const gravarConversas = (conversas: Conversas, atual?: { app: string; conversa: string }) => {
  const tentar = () => gravarItem(CHAVE, JSON.stringify(conversas));
  try {
    tentar();
    return;
  } catch (erro) {
    if (!ehFaltaDeEspaco(erro)) throw erro;
  }

  compactarTudo(conversas);
  try {
    tentar();
    return;
  } catch (erro) {
    if (!ehFaltaDeEspaco(erro)) throw erro;
  }

  // As chaves de um objeto mantêm a ordem de criação: as primeiras são as conversas mais antigas.
  const antigas = Object.entries(conversas).flatMap(([app, conversasDoApp]) =>
    Object.keys(conversasDoApp)
      .filter((id) => !(app === atual?.app && id === atual?.conversa))
      .map((id) => [app, id] as const)
  );
  for (const [app, id] of antigas) {
    delete conversas[app][id];
    if (!Object.keys(conversas[app]).length) delete conversas[app];
    try {
      tentar();
      return;
    } catch (erro) {
      if (!ehFaltaDeEspaco(erro)) throw erro;
    }
  }
  tentar(); // só a conversa atual e ainda não cabe: deixa o erro aparecer
};
